import React, { ReactNode } from 'react';

export interface SearchablePickerProps<T> {
  // Data
  items: T[];
  getKey: (item: T) => string | number;
  onSelect: (item: T) => void;

  // Search (controlled)
  searchTerm: string;
  onSearchTermChange: (term: string) => void;

  // Filtering/sorting/rendering
  filter?: (item: T, term: string) => boolean;
  sort?: (a: T, b: T, term: string) => number;
  renderItem: (item: T, term: string) => ReactNode;

  // Create new option
  showCreateNew?: boolean;
  onCreateNew?: (term: string) => void;
  createNewLabel?: (term: string) => string;
  // Control whether to show the list. Input is always rendered.
  showList?: boolean;

  // UI text
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;

  // Event handlers
  onInputFocus?: () => void;

  // Classes to integrate with host styles
  fieldContainerClassName?: string; // wraps the input + label
  inputClassName?: string;
  listClassName?: string;
  itemClassName?: string;
  newItemClassName?: string;
}

const defaultCreateNewLabel = (term: string) => `+ Add "${term}"`;

function SearchablePicker<T>(props: SearchablePickerProps<T>) {
  const {
    items,
    getKey,
    onSelect,
    searchTerm,
    onSearchTermChange,
    filter,
    sort,
    renderItem,
    showCreateNew = false,
    onCreateNew,
    createNewLabel = defaultCreateNewLabel,
    showList = true,
    label,
    placeholder,
    autoFocus,
    onInputFocus,
    fieldContainerClassName,
    inputClassName,
    listClassName,
    itemClassName,
    newItemClassName,
  } = props;

  const filtered = React.useMemo(() => {
    const term = searchTerm.trim();
    const base = term
      ? (filter ? items.filter((i) => filter(i, term)) : items)
      : [];
    return sort ? [...base].sort((a, b) => sort(a, b, term)) : base;
  }, [items, searchTerm, filter, sort]);

  return (
    <>
      <div className={fieldContainerClassName}>
        {label && <label>{label}</label>}
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          onFocus={onInputFocus}
          placeholder={placeholder}
          className={inputClassName}
          autoFocus={autoFocus}
        />
      </div>

      {searchTerm && showList && (
        <div className={listClassName}>
          {showCreateNew && onCreateNew && (
            <div
              className={newItemClassName}
              onClick={() => onCreateNew(searchTerm)}
            >
              {createNewLabel(searchTerm)}
            </div>
          )}

          {filtered.map((item) => (
            <div
              key={String(getKey(item))}
              className={itemClassName}
              onClick={() => onSelect(item)}
            >
              {renderItem(item, searchTerm)}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default SearchablePicker;


